import {
  BaseDataGrid,
  ButtonRow,
  DATE_FORMAT_ISO_NO_TIME,
  Dayjs,
  getFilteredColDefs,
  useGridServerPagination,
  useSearchParamsDate,
  useSearchParamsForm,
} from "@synota-io/synota-shared-ui";
import { generatePath, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { IInvoice } from "../../../../api/hooks/useInvoices";
import {
  DataGridProps,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { TableSummaryToolbar } from "../../../../shared/datagrid/TableSummaryToolbar";
import { TableDateFiltersToolbar } from "../../../../shared/datagrid/TableDateFiltersToolbar";
import { Button } from "@mui/material";
import { CONTRACT_INVOICES_PATH, CONTRACTS_DETAIL_PATH } from "../../../../paths";
import { serviceChargeInvoiceFields } from "../fields/serviceChargeInvoiceFields";
import { useServiceChargeInvoices } from "../../../../api/hooks/useServiceChargeInvoices";
import {
  DashboardContractSearchOption,
  DashboardContractSearchField,
} from "../../../../shared/form/DashboardContractSearchField";
import { useSearchParamsDashboardContract } from "../../users/hooks/useSearchParamsDashboardContract";
import {
  DashboardCounterpartySearchField,
  DashboardCounterpartySearchOption,
} from "../../../../shared/form/DashboardCounterpartySearchField";
import { useSearchParamsDashboardCounterparty } from "../../users/hooks/useSearchParamsDashboardCounterparty";
import { useServiceChargeInvoicesReport } from "../../../../api/hooks/useServiceChargeInvoicesReport";

type Props = Pick<DataGridProps<IInvoice>, "slots" | "slotProps" | "onRowDoubleClick"> & {
  summary?: boolean;
};

export const ServiceChargeInvoicesTable = ({ summary, ...props }: Props) => {
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const params = useParams<{
    id: string;
  }>();

  const { state } = useLocation();
  const { control, watch } = useSearchParamsForm<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    contract: DashboardContractSearchOption;
    counterparty: DashboardCounterpartySearchOption;
  }>(
    {
      defaultValues: {
        contract: null,
        counterparty: null,
        end_date: null,
        start_date: null,
      },
    },
    {
      contract: useSearchParamsDashboardContract({ uuid: params.id }),
      counterparty: useSearchParamsDashboardCounterparty({ uuid: params.id }),
      start_date: useSearchParamsDate(),
      end_date: useSearchParamsDate(),
    },
  );

  const navigate = useNavigate();
  const values = watch();

  const [paginationModel, onPaginationModelChange] = useState<GridPaginationModel>(() =>
    summary
      ? { page: 0, pageSize: 5 }
      : {
          page: 0,
          pageSize: 30,
        },
  );

  const input = {
    user_uuid: params.id || "",
    contract_shared_uuid: values.contract?.value,
    counterparty_email: values.counterparty?.value,
    energy_date_start: values.start_date?.format(DATE_FORMAT_ISO_NO_TIME),
    energy_date_end: values.end_date?.format(DATE_FORMAT_ISO_NO_TIME),
  };

  const {
    invoices,
    hasNextPage,
    isLoading: isLoadingInvoices,
  } = useServiceChargeInvoices({
    ...input,
    page: paginationModel.page,
    number_to_return: paginationModel.pageSize,
  });

  const paginationProps = useGridServerPagination({
    hasNextPage,
    currentPageRowCount: invoices.length,
    paginationModel,
    onPaginationModelChange,
  });

  const { download, isPending: isPendingReport } = useServiceChargeInvoicesReport(input);

  const columns = useMemo(
    () =>
      getFilteredColDefs(serviceChargeInvoiceFields, summary ? "summary" : "table", summary).map(
        (column) => ({
          ...column,
          flex: 1,
        }),
      ),
    [summary],
  );

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      onRowSelectionModelChange([state.selectedRow]);
    }
  }, [state]);

  const isLoading = isLoadingInvoices || isPendingReport;

  return (
    <BaseDataGrid
      {...props}
      slots={{
        footer: summary
          ? () => (
              <ButtonRow borderTop="solid" borderColor="background.default">
                <Button
                  component={Link}
                  to={generatePath(CONTRACT_INVOICES_PATH, { id: params.id! })}
                  size="small"
                  fullWidth
                >
                  See all
                </Button>
              </ButtonRow>
            )
          : undefined,
        toolbar: summary
          ? TableSummaryToolbar
          : (props) => (
              <TableDateFiltersToolbar
                onDownloadClick={download}
                control={control}
                isLoading={isLoading}
                {...props}
              >
                <DashboardContractSearchField
                  sx={{ width: "24em" }}
                  disabled={isLoading}
                  size="small"
                  label="Contract"
                  variant="filled"
                  control={control}
                  name="contract"
                  onContractOpen={(_e, contract) => {
                    navigate(generatePath(CONTRACTS_DETAIL_PATH, { id: contract.value }));
                  }}
                />
                {params.id ? (
                  <DashboardCounterpartySearchField
                    sx={{ width: "24em" }}
                    disabled={isLoading}
                    size="small"
                    label="Payer"
                    variant="filled"
                    control={control}
                    name="counterparty"
                    uuid={params.id}
                  />
                ) : null}
              </TableDateFiltersToolbar>
            ),
        ...props.slots,
      }}
      density={summary ? "compact" : "standard"}
      getRowId={(row) => row.shared_uuid}
      columns={columns}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={setColumnVisibility}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      autosizeOnMount={!summary}
      {...paginationProps}
      loading={isLoading}
      rows={invoices}
    />
  );
};
